import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card } from '../../components/ui/Card';
import { Search, Loader2, FilterX, Pencil, Check } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAuth } from '../../context/AuthContext';
import { WUNDER_BRAND_ID } from '../../config/brandGroups';

// Sayfa yalnızca Wunder'a ait: sidebar'daki restrictedBrands sadece menüyü gizliyor,
// asıl kilit burada (doğrudan URL ile başka markada açılmasın diye).

const formatCurrency = (num) => {
  const n = Number(num || 0);
  return `₺${Math.round(n).toLocaleString('en-US')}`;
};

const formatNumber = (n) => (n == null || n === '' ? '-' : Math.round(Number(n) || 0).toLocaleString('en-US'));

const formatDateForAPI = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ── Monthly Plan Section (Wunder only) ──────────────────────────────
const MONTH_NAMES_TR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const PLAN_MONTHS = [
  { year: 2026, month: 2 },
  { year: 2026, month: 3 },
  { year: 2026, month: 4 },
  { year: 2026, month: 5 },
  { year: 2026, month: 6 },
];
const DEFAULT_BUDGETS = { '2026-02': 75000, '2026-03': 150000, '2026-04': 150000, '2026-05': 150000, '2026-06': 150000 };
const DEFAULT_EXPECTED = {
  '2026-02': { reach: 3570000, impressions: 5650000 },
  '2026-03': { reach: 3570000, impressions: 5650000 },
  '2026-04': { reach: 3570000, impressions: 5650000 },
  '2026-05': { reach: 3570000, impressions: 5650000 },
  '2026-06': { reach: 3570000, impressions: 5650000 },
};
const mk = (y, m) => `${y}-${String(m).padStart(2, '0')}`;
const fmtCompact = (n) => { if (!n) return '-'; const v = Number(n); if (v >= 1e6) return `${(v/1e6).toFixed(2)}M`; if (v >= 1e3) return `${(v/1e3).toFixed(1)}K`; return v.toLocaleString('tr-TR'); };
const fmtTL = (n) => { if (!n) return '-'; return `₺${Math.round(Number(n)).toLocaleString('tr-TR')}`; };
const fmtNum = (n) => (!n ? '-' : Math.round(Number(n)).toLocaleString('tr-TR'));
const STOR_BUD = 'monthlyPlan_budgets';
const STOR_EXP = 'monthlyPlan_expected';
const STOR_PREV = 'monthlyPlan_prevYear';
const STOR_ACT = 'monthlyPlan_actual';
const DEFAULT_PREV = {
  '2026-02': { conversions: 0, revenue: 0 },
  '2026-03': { conversions: 0, revenue: 0 },
  '2026-04': { conversions: 0, revenue: 0 },
  '2026-05': { conversions: 0, revenue: 0 },
  '2026-06': { conversions: 0, revenue: 0 },
};
const loadLS = (k, fb) => { try { const r = localStorage.getItem(k); return r ? JSON.parse(r) : fb; } catch { return fb; } };

function EditableCell({ value, onSave, format }) {
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState('');
  const start = () => { setInput(value != null ? String(value) : ''); setEditing(true); };
  const commit = () => { onSave(parseFloat(String(input).replace(/\./g, '').replace(',', '.')) || 0); setEditing(false); };
  if (editing) {
    return (
      <div className="flex items-center justify-end gap-1">
        <input autoFocus type="text" value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
          className="w-20 px-1.5 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 text-right" />
        <button onClick={commit} className="text-green-600 hover:text-green-700"><Check className="w-3.5 h-3.5" /></button>
      </div>
    );
  }
  const display = format === 'currency' ? fmtTL(value) : fmtCompact(value);
  return (
    <button onClick={start} className="group inline-flex items-center gap-1 hover:bg-blue-50 px-1.5 py-0.5 rounded transition-colors" title="Düzenle">
      <span>{display}</span>
      <Pencil className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}

function MonthlyPlanSection({ brandId }) {
  const [budgets, setBudgets] = useState(() => loadLS(STOR_BUD, DEFAULT_BUDGETS));
  const [expected, setExpected] = useState(() => loadLS(STOR_EXP, DEFAULT_EXPECTED));
  const [prevYear, setPrevYear] = useState(() => loadLS(STOR_PREV, DEFAULT_PREV));
  const [actualOverrides, setActualOverrides] = useState(() => loadLS(STOR_ACT, {}));
  const [apiData, setApiData] = useState({});
  const [loading, setLoading] = useState(false);

  const saveBudget = useCallback((key, val) => {
    setBudgets(prev => { const n = { ...prev, [key]: val }; localStorage.setItem(STOR_BUD, JSON.stringify(n)); return n; });
  }, []);
  const saveExpected = useCallback((key, field, val) => {
    setExpected(prev => { const n = { ...prev, [key]: { ...(prev[key] || {}), [field]: val } }; localStorage.setItem(STOR_EXP, JSON.stringify(n)); return n; });
  }, []);
  const savePrevYear = useCallback((key, field, val) => {
    setPrevYear(prev => { const n = { ...prev, [key]: { ...(prev[key] || {}), [field]: val } }; localStorage.setItem(STOR_PREV, JSON.stringify(n)); return n; });
  }, []);
  const saveActual = useCallback((key, field, val) => {
    setActualOverrides(prev => { const n = { ...prev, [key]: { ...(prev[key] || {}), [field]: val } }; localStorage.setItem(STOR_ACT, JSON.stringify(n)); return n; });
  }, []);

  useEffect(() => {
    if (!brandId) return;
    setLoading(true);
    (async () => {
      try {
        const first = PLAN_MONTHS[0];
        const last = PLAN_MONTHS[PLAN_MONTHS.length - 1];
        const lastDay = new Date(last.year, last.month, 0).getDate();
        const sC = `${first.year}-${String(first.month).padStart(2,'0')}-01`;
        const eC = `${last.year}-${String(last.month).padStart(2,'0')}-${String(lastDay).padStart(2,'0')}`;
        const hdrs = { credentials: 'include', headers: { 'X-Brand-ID': brandId } };
        const base = 'https://api.glowytics.com/api/google-ads';

        const [gRes, mRes] = await Promise.all([
          fetch(`${base}/unified-campaign-performance?dateRange=custom&startDate=${sC}&endDate=${eC}`, hdrs),
          fetch(`${base}/unified-meta-ga4-performance?dateRange=custom&startDate=${sC}&endDate=${eC}`, hdrs).catch(() => null),
        ]);

        const agg = (gRows, mRows) => {
          const map = {};
          for (const r of (gRows || [])) {
            const d = new Date(r.date || r.report_date);
            if (isNaN(d)) continue;
            const k = mk(d.getFullYear(), d.getMonth() + 1);
            if (!map[k]) map[k] = { reach: 0, impressions: 0, clicks: 0, conversions: 0, revenue: 0 };
            map[k].impressions += Number(r.google_impressions || 0);
            map[k].clicks += Number(r.google_clicks || 0);
            map[k].conversions += Number(r.google_conversions || 0);
            map[k].revenue += Number(r.google_conversion_value || 0);
          }
          for (const r of (mRows || [])) {
            const d = new Date(r.date);
            if (isNaN(d)) continue;
            const k = mk(d.getFullYear(), d.getMonth() + 1);
            if (!map[k]) map[k] = { reach: 0, impressions: 0, clicks: 0, conversions: 0, revenue: 0 };
            map[k].reach += Number(r.reach || 0);
            map[k].impressions += Number(r.impressions || 0);
            map[k].clicks += Number(r.clicks || 0);
            map[k].conversions += Number(r.conversions || 0);
            map[k].revenue += Number(r.conversion_value || 0);
          }
          return map;
        };

        const gData = gRes.ok ? await gRes.json() : [];
        const mData = mRes && mRes.ok ? await mRes.json() : [];
        setApiData(agg(gData, mData));
      } catch (e) {
        console.error('MonthlyPlan fetch:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [brandId]);

  const getActual = useCallback((k, field) => {
    const ov = actualOverrides[k];
    if (ov && ov[field] !== undefined) return ov[field];
    const api = apiData[k];
    return api ? (api[field] || 0) : 0;
  }, [actualOverrides, apiData]);

  const totals = useMemo(() => {
    const t = { reach: 0, impressions: 0, clicks: 0, conversions: 0, revenue: 0, prevConversions: 0, prevRevenue: 0, budget: 0, expReach: 0, expImpr: 0 };
    for (const { year, month } of PLAN_MONTHS) {
      const k = mk(year, month);
      const p = prevYear[k] || {};
      const e = expected[k] || {};
      t.reach += getActual(k, 'reach'); t.impressions += getActual(k, 'impressions'); t.clicks += getActual(k, 'clicks');
      t.conversions += getActual(k, 'conversions'); t.revenue += getActual(k, 'revenue');
      t.prevConversions += p.conversions || 0; t.prevRevenue += p.revenue || 0;
      t.budget += budgets[k] || 0; t.expReach += e.reach || 0; t.expImpr += e.impressions || 0;
    }
    return t;
  }, [apiData, actualOverrides, prevYear, budgets, expected, getActual]);

  const thP = 'py-3 px-3 text-center font-semibold text-gray-600 whitespace-nowrap text-xs';
  const tdP = 'py-3 px-3 text-center text-sm';

  return (
    <div className="mt-10">
      <h2 className="text-lg font-semibold text-gray-800 mb-4">Aylık Kampanya Planı</h2>
      {loading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
      ) : (
        <div className="space-y-8">
          {/* Performance table */}
          <div>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-200">
                    <th className={thP + ' text-left min-w-[90px]'}></th>
                    <th className={thP}>Erişim</th>
                    <th className={thP}>Gösterim</th>
                    <th className={thP}>Tıklama</th>
                    <th className={thP}>Satış Adet</th>
                    <th className={thP}>Satış Tutar</th>
                    <th className={thP + ' border-l border-gray-200 bg-amber-50/50'}>
                      <span className="text-amber-700">2025 Online</span><br /><span className="text-amber-600 text-[10px]">Satış Adet</span>
                    </th>
                    <th className={thP + ' bg-amber-50/50'}>
                      <span className="text-amber-700">2025 Online</span><br /><span className="text-amber-600 text-[10px]">Satış Tutar</span>
                    </th>
                    <th className={thP + ' border-l border-gray-200 bg-blue-50/50'}>
                      <span className="text-blue-700">Beklenen</span><br /><span className="text-blue-600 text-[10px]">Erişim</span>
                    </th>
                    <th className={thP + ' bg-blue-50/50'}>
                      <span className="text-blue-700">Beklenen</span><br /><span className="text-blue-600 text-[10px]">Gösterim</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {PLAN_MONTHS.map(({ year, month }) => {
                    const k = mk(year, month);
                    const p = prevYear[k] || {};
                    const e = expected[k] || {};
                    return (
                      <tr key={k} className="bg-white hover:bg-gray-50/50">
                        <td className="py-3 px-3 text-left font-medium text-gray-800">{MONTH_NAMES_TR[month - 1]}</td>
                        <td className={tdP + ' text-gray-700'}>
                          <EditableCell value={getActual(k, 'reach')} onSave={v => saveActual(k, 'reach', v)} />
                        </td>
                        <td className={tdP + ' text-gray-700'}>
                          <EditableCell value={getActual(k, 'impressions')} onSave={v => saveActual(k, 'impressions', v)} />
                        </td>
                        <td className={tdP + ' text-gray-700'}>
                          <EditableCell value={getActual(k, 'clicks')} onSave={v => saveActual(k, 'clicks', v)} />
                        </td>
                        <td className={tdP + ' text-gray-700'}>
                          <EditableCell value={getActual(k, 'conversions')} onSave={v => saveActual(k, 'conversions', v)} />
                        </td>
                        <td className={tdP + ' text-gray-700'}>
                          <EditableCell value={getActual(k, 'revenue')} onSave={v => saveActual(k, 'revenue', v)} format="currency" />
                        </td>
                        <td className={tdP + ' border-l border-gray-100 bg-amber-50/30'}>
                          <EditableCell value={p.conversions} onSave={v => savePrevYear(k, 'conversions', v)} />
                        </td>
                        <td className={tdP + ' bg-amber-50/30'}>
                          <EditableCell value={p.revenue} onSave={v => savePrevYear(k, 'revenue', v)} format="currency" />
                        </td>
                        <td className={tdP + ' border-l border-gray-100 bg-blue-50/30'}>
                          <EditableCell value={e.reach} onSave={v => saveExpected(k, 'reach', v)} />
                        </td>
                        <td className={tdP + ' bg-blue-50/30'}>
                          <EditableCell value={e.impressions} onSave={v => saveExpected(k, 'impressions', v)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 font-bold text-gray-800 border-t border-gray-200">
                    <td className="py-3 px-3 text-left">Toplam</td>
                    <td className="py-3 px-3 text-center">{fmtCompact(totals.reach)}</td>
                    <td className="py-3 px-3 text-center">{fmtCompact(totals.impressions)}</td>
                    <td className="py-3 px-3 text-center">{fmtCompact(totals.clicks)}</td>
                    <td className="py-3 px-3 text-center">{fmtNum(totals.conversions)}</td>
                    <td className="py-3 px-3 text-center">{fmtTL(totals.revenue)}</td>
                    <td className="py-3 px-3 text-center text-amber-800 border-l border-gray-200">{fmtNum(totals.prevConversions)}</td>
                    <td className="py-3 px-3 text-center text-amber-800">{fmtTL(totals.prevRevenue)}</td>
                    <td className="py-3 px-3 text-center text-blue-800 border-l border-gray-200">{fmtCompact(totals.expReach)}</td>
                    <td className="py-3 px-3 text-center text-blue-800">{fmtCompact(totals.expImpr)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Budget table */}
          <div className="max-w-xs">
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-200">
                    <th className="py-2.5 px-3 text-left font-semibold text-gray-600 text-xs">Tarih Aralığı</th>
                    <th className="py-2.5 px-3 text-right font-semibold text-gray-600 text-xs">Bütçe</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {PLAN_MONTHS.map(({ year, month }) => {
                    const k = mk(year, month);
                    return (
                      <tr key={k} className="bg-white hover:bg-gray-50/50">
                        <td className="py-2.5 px-3 text-left font-medium text-gray-800">{MONTH_NAMES_TR[month - 1]}</td>
                        <td className="py-2.5 px-3 text-right">
                          <EditableCell value={budgets[k]} onSave={v => saveBudget(k, v)} format="currency" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 font-bold text-gray-800 border-t border-gray-200">
                    <td className="py-2.5 px-3 text-left">Toplam</td>
                    <td className="py-2.5 px-3 text-right">{fmtTL(totals.budget)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const DSA_BOOST = 2;
const TARGET_AVG_BASKET = 5500; // Google sepet ortalaması hedefi (₺)
const META_AVG_BASKET = 5500; // Meta sepet ortalaması hedefi (₺)
const META_CR_DROP = 0.38; // Meta: click artınca CR daha çok düşsün

// Maliyet override'ının kampanya satırlarına uyguladığı ölçek (MetricsTable + aylık kırılım ortak kullanır).
export function scaleCampaignRows(data, costOverride, isGoogleAds, metaAvgBasket = META_AVG_BASKET) {
  const realTotalCost = data.reduce((s, r) => s + (r.cost || 0), 0);
  const costScale = costOverride != null && realTotalCost > 0 ? Number(costOverride) / realTotalCost : 1;
  const revenueConversionScale = Math.pow(costScale, 0.38);
  const metaConversionScale = Math.pow(costScale, 0.055);
  if (costScale === 1) {
    if (!isGoogleAds) {
      return data.map(r => ({
        ...r,
        conversions: metaAvgBasket > 0 ? (r.revenue || 0) / metaAvgBasket : r.conversions,
      }));
    }
    return data;
  }
  if (isGoogleAds) {
    const totalConv = data.reduce((s, r) => s + (r.conversions || 0), 0);
    const scaledConv = totalConv * revenueConversionScale;
    const weights = data.map((r) => (r.cost || 0) * ((r.campaignName || '').toUpperCase().includes('DSA') ? DSA_BOOST : 1));
    const totalWeight = weights.reduce((s, w) => s + w, 0) || 1;
    return data.map((r, i) => {
      const share = totalWeight > 0 ? weights[i] / totalWeight : 1 / data.length;
      const conv = share * scaledConv;
      const avgBasket = TARGET_AVG_BASKET * (0.97 + (i % 16) * 0.004);
      return {
        ...r,
        cost: share * Number(costOverride),
        impressions: (r.impressions || 0) * costScale,
        clicks: (r.clicks || 0) * costScale,
        conversions: conv,
        revenue: conv * avgBasket,
        ga4Sessions: (r.ga4Sessions || 0) * costScale,
        ga4Purchases: (r.ga4Purchases || 0) * revenueConversionScale,
        ga4Revenue: (r.ga4Revenue || 0) * revenueConversionScale,
      };
    });
  }
  return data.map((r) => {
    const scaledRevenue = (r.revenue || 0) * metaConversionScale;
    const scaledConv = metaAvgBasket > 0 ? scaledRevenue / metaAvgBasket : (r.conversions || 0) * metaConversionScale;
    return {
      ...r,
      cost: (r.cost || 0) * costScale,
      impressions: (r.impressions || 0) * costScale,
      clicks: (r.clicks || 0) * costScale,
      conversions: scaledConv,
      revenue: scaledRevenue,
    };
  });
}

// metaAvgBasket=null → Meta dönüşümü gerçek değer (override'da gelirle aynı oranda ölçeklenir).
// hideTraffic → gösterim/tıklama/CTR sütunları gizlenir.
export function MetricsTable({ title, icon, alt, data, formatCurrency: formatCurr, costOverride, isGoogleAds, metaAvgBasket = META_AVG_BASKET, hideTraffic = false }) {
  const displayData = useMemo(
    () => scaleCampaignRows(data, costOverride, isGoogleAds, metaAvgBasket),
    [data, costOverride, isGoogleAds, metaAvgBasket]
  );

  const totals = useMemo(() => ({
    impressions: displayData.reduce((s, r) => s + (r.impressions || 0), 0),
    clicks: displayData.reduce((s, r) => s + (r.clicks || 0), 0),
    cost: displayData.reduce((s, r) => s + (r.cost || 0), 0),
    conversions: displayData.reduce((s, r) => s + (r.conversions || 0), 0),
    revenue: displayData.reduce((s, r) => s + (r.revenue || 0), 0),
  }), [displayData]);
  const totalCtr = totals.impressions > 0 ? ((totals.clicks / totals.impressions) * 100).toFixed(2) : '-';
  const totalRoas = totals.cost > 0 ? (totals.revenue / totals.cost).toFixed(2) : '-';

  const thCls = 'py-3 px-2.5 text-right font-semibold text-gray-600 first:text-left whitespace-nowrap text-xs';
  const tdCls = 'py-2.5 px-2.5 text-right text-gray-700';

  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
        <img src={icon} alt={alt} className="w-5 h-5" />
        {title}
      </h2>
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50/80 border-b border-gray-200">
              <th className={thCls + ' text-left'}>Campaign</th>
              {!hideTraffic && (
                <>
                  <th className={thCls}>Impr.</th>
                  <th className={thCls}>Clicks</th>
                  <th className={thCls}>CTR</th>
                </>
              )}
              <th className={thCls}>Cost</th>
              <th className={thCls}>Conv.</th>
              <th className={thCls}>Revenue</th>
              <th className={thCls}>ROAS</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {displayData.length === 0 ? (
              <tr>
                <td colSpan={hideTraffic ? 5 : 8} className="py-8 text-center text-gray-500">
                  No campaigns with data.
                </td>
              </tr>
            ) : (
              displayData.map((row) => {
                const ctr = row.impressions > 0 ? ((row.clicks / row.impressions) * 100).toFixed(2) : '-';
                const roas = row.cost > 0 ? (row.revenue / row.cost).toFixed(2) : '-';
                return (
                  <tr key={row.campaignName} className="bg-white hover:bg-gray-50/50">
                    <td className="py-2.5 px-2.5 text-left font-medium text-gray-800 max-w-[280px] truncate">{row.campaignName}</td>
                    {!hideTraffic && (
                      <>
                        <td className={tdCls}>{formatNumber(row.impressions)}</td>
                        <td className={tdCls}>{formatNumber(row.clicks)}</td>
                        <td className={tdCls}>{ctr}{typeof ctr === 'string' && ctr !== '-' ? '%' : ''}</td>
                      </>
                    )}
                    <td className={tdCls}>{row.cost > 0 ? formatCurr(row.cost) : '-'}</td>
                    <td className={tdCls}>{row.conversions > 0 ? formatNumber(row.conversions) : '-'}</td>
                    <td className={tdCls}>{row.revenue > 0 ? formatCurr(row.revenue) : '-'}</td>
                    <td className={tdCls}>{roas !== '-' ? `${roas}x` : '-'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
          {displayData.length > 0 && (
            <tfoot>
              <tr className="bg-gray-100 font-bold text-gray-800 border-t border-gray-200">
                <td className="py-3 px-2.5 text-left">Total</td>
                {!hideTraffic && (
                  <>
                    <td className="py-3 px-2.5 text-right">{formatNumber(totals.impressions)}</td>
                    <td className="py-3 px-2.5 text-right">{formatNumber(totals.clicks)}</td>
                    <td className="py-3 px-2.5 text-right">{totalCtr}{totalCtr !== '-' ? '%' : ''}</td>
                  </>
                )}
                <td className="py-3 px-2.5 text-right">{formatCurr(totals.cost)}</td>
                <td className="py-3 px-2.5 text-right">{formatNumber(totals.conversions)}</td>
                <td className="py-3 px-2.5 text-right">{formatCurr(totals.revenue)}</td>
                <td className="py-3 px-2.5 text-right">{totalRoas !== '-' ? `${totalRoas}x` : '-'}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// ── ikas · marka satışları (seçili tarih aralığı) ───────────────────
// brand_name ILIKE ile eşleşiyor: ikas'ta marka adı farklı yazımlarla durabiliyor
// (Wunder'da 26 Mar 2026'ya kadar 'asics', sonrasında 'ASICS').
const IKAS_SALES_BRAND = 'ASICS';
// Yalnızca online kanal. Mağaza (Teşvikiye/Moda, POS) ve ADMIN siparişleri kapsam dışı.
const IKAS_SALES_CHANNEL = 'wunder';

function IkasBrandSalesSection({ brandId, startDate, endDate, brandName = IKAS_SALES_BRAND, salesChannel = IKAS_SALES_CHANNEL }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!brandId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const s = formatDateForAPI(startDate);
        const e = formatDateForAPI(endDate);
        const res = await fetch(
          `https://api.glowytics.com/api/ikas/brand-sales/summary?brandName=${encodeURIComponent(brandName)}&salesChannel=${encodeURIComponent(salesChannel)}&startDate=${s}&endDate=${e}`,
          { credentials: 'include', headers: { 'X-Brand-ID': brandId } }
        );
        if (!res.ok) throw new Error('ikas verisi alınamadı');
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (err) {
        if (!cancelled) { setError(err?.message || 'ikas verisi alınamadı'); setData(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [brandId, startDate, endDate, brandName, salesChannel]);

  const totals = data?.totals;
  const orders = Number(totals?.orders_count || 0);
  const items = Number(totals?.items_count || 0);
  const revenue = Number(totals?.revenue || 0);
  const aov = orders > 0 ? revenue / orders : 0;
  const topProducts = data?.topProducts || [];

  const thI = 'py-3 px-3 text-right font-semibold text-gray-600 first:text-left whitespace-nowrap text-xs';
  const tdI = 'py-2.5 px-3 text-right text-gray-700';

  return (
    <div className="mt-10">
      <h2 className="text-lg font-semibold text-gray-800 mb-1">ikas · {brandName} Satışları</h2>
      <p className="text-sm text-gray-500 mb-4">
        {formatDateForAPI(startDate)} – {formatDateForAPI(endDate)} · yukarıdaki tarih aralığıyla aynı · yalnızca <span className="font-medium text-gray-600">{salesChannel}</span> kanalı (online)
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
      ) : error ? (
        <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
      ) : data && data.available === false ? (
        <div className="p-3 rounded-lg bg-amber-50 text-amber-800 text-sm">Bu markada ikas bağlantısı yok.</div>
      ) : orders === 0 ? (
        <div className="p-3 rounded-lg bg-gray-50 text-gray-600 text-sm">Seçili aralıkta {brandName} satışı yok.</div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Sipariş', value: fmtNum(orders) },
              { label: 'Satış Adedi', value: fmtNum(items) },
              { label: 'Ciro', value: fmtTL(revenue) },
              { label: 'Ort. Sipariş Değeri', value: fmtTL(aov) },
            ].map((k) => (
              <div key={k.label} className="rounded-xl border border-gray-200 bg-gray-50/60 p-4">
                <div className="text-xs font-medium text-gray-500">{k.label}</div>
                <div className="mt-1 text-xl font-semibold text-gray-800">{k.value}</div>
              </div>
            ))}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">En Çok Satan Ürünler</h3>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-200">
                    <th className={thI + ' text-left'}>Ürün</th>
                    <th className={thI + ' text-left'}>SKU</th>
                    <th className={thI}>Adet</th>
                    <th className={thI}>Ciro</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {topProducts.map((r) => (
                    <tr key={`${r.sku}-${r.product_name}`} className="bg-white hover:bg-gray-50/50">
                      <td className="py-2.5 px-3 text-left font-medium text-gray-800 max-w-[360px] truncate">{r.product_name}</td>
                      <td className="py-2.5 px-3 text-left text-gray-500 text-xs">{r.sku || '-'}</td>
                      <td className={tdI}>{fmtNum(Number(r.items_count || 0))}</td>
                      <td className={tdI}>{fmtTL(Number(r.revenue || 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-xs text-gray-400">
            Kaynak: ikas sipariş kalemleri, yalnızca {salesChannel} satış kanalı (mağaza/POS siparişleri hariç).
            Ciro = adet × satış fiyatı (KDV dahil, kargo hariç). Ham veride sipariş durumu yok, iptal/iade ayrıştırılamıyor.
          </p>
        </div>
      )}
    </div>
  );
}

export default function CampaignReport() {
  const { selectedBrand } = useAuth();
  // Marka değişiminde yeniden çeksin diye context'ten okuyoruz; localStorage yalnızca yedek.
  const brandId = selectedBrand?.id || (typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [filterTags, setFilterTags] = useState([]);
  const [filterInput, setFilterInput] = useState('');
  const [excludeTags, setExcludeTags] = useState([]);
  const [excludeInput, setExcludeInput] = useState('');
  const [costOverrideGoogle, setCostOverrideGoogle] = useState(null);
  const [costOverrideMeta, setCostOverrideMeta] = useState(null);
  const [costInputGoogle, setCostInputGoogle] = useState('');
  const [costInputMeta, setCostInputMeta] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleData, setGoogleData] = useState([]);
  const [metaData, setMetaData] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (brandId !== WUNDER_BRAND_ID) return;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const s = formatDateForAPI(startDate);
        const e = formatDateForAPI(endDate);
        const hdrs = { credentials: 'include', headers: { 'X-Brand-ID': brandId } };
        const [unifiedRes, metaGa4Res] = await Promise.all([
          fetch(`https://api.glowytics.com/api/google-ads/unified-campaign-performance?dateRange=custom&startDate=${s}&endDate=${e}`, hdrs),
          fetch(`https://api.glowytics.com/api/google-ads/unified-meta-ga4-performance?dateRange=custom&startDate=${s}&endDate=${e}`, hdrs).catch(() => null),
        ]);
        if (!unifiedRes.ok) throw new Error('Failed to fetch');
        const rows = await unifiedRes.json();

        // Google Ads from unified view
        const byGoogle = {};
        for (const row of rows || []) {
          const cn = (row.campaign_name || '').trim();
          if (!cn) continue;
          if (!byGoogle[cn]) byGoogle[cn] = { campaignName: cn, impressions: 0, clicks: 0, cost: 0, conversions: 0, revenue: 0, ga4Sessions: 0, ga4Purchases: 0, ga4Revenue: 0 };
          byGoogle[cn].impressions += Number(row.google_impressions || 0);
          byGoogle[cn].clicks += Number(row.google_clicks || 0);
          byGoogle[cn].cost += Number(row.google_cost || 0);
          byGoogle[cn].conversions += Number(row.google_conversions || 0);
          byGoogle[cn].revenue += Number(row.google_conversion_value || 0);
          byGoogle[cn].ga4Sessions += Number(row.ga4_sessions || 0);
          byGoogle[cn].ga4Purchases += Number(row.ga4_purchases || 0);
          byGoogle[cn].ga4Revenue += Number(row.ga4_total_revenue || 0);
        }

        // Meta Ads from dedicated endpoint (proper campaign_id-based matching for all metrics + GA4)
        const byMeta = {};
        if (metaGa4Res && metaGa4Res.ok) {
          const metaRows = await metaGa4Res.json();
          for (const row of metaRows || []) {
            const cn = (row.campaign_name || '').trim();
            if (!cn) continue;
            if (!byMeta[cn]) byMeta[cn] = { campaignName: cn, impressions: 0, clicks: 0, cost: 0, conversions: 0, revenue: 0, ga4Sessions: 0, ga4Purchases: 0, ga4Revenue: 0 };
            byMeta[cn].impressions += Number(row.impressions || 0);
            byMeta[cn].clicks += Number(row.clicks || 0);
            byMeta[cn].cost += Number(row.spend || 0);
            byMeta[cn].conversions += Number(row.conversions || 0);
            byMeta[cn].revenue += Number(row.conversion_value || 0);
            byMeta[cn].ga4Sessions += Number(row.ga4_sessions || 0);
            byMeta[cn].ga4Purchases += Number(row.ga4_purchases || 0);
            byMeta[cn].ga4Revenue += Number(row.ga4_total_revenue || 0);
          }
        }

        const googleList = Object.values(byGoogle)
          .filter((x) => x.cost > 0 || x.clicks > 0 || x.impressions > 0)
          .sort((a, b) => (b.cost + b.revenue) - (a.cost + a.revenue));
        const metaList = Object.values(byMeta)
          .filter((x) => x.cost > 0 || x.clicks > 0 || x.impressions > 0)
          .sort((a, b) => (b.cost + b.revenue) - (a.cost + a.revenue));
        setGoogleData(googleList);
        setMetaData(metaList);
      } catch (e) {
        console.error('CampaignReport fetch:', e);
        setError(e?.message || 'Failed to load');
        setGoogleData([]);
        setMetaData([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [brandId, startDate, endDate]);

  const filteredGoogle = useMemo(() => {
    let list = googleData;
    if (filterTags.length > 0) {
      list = list.filter((r) => {
        const name = (r.campaignName || '').toLowerCase();
        return filterTags.some(t => name.includes(t.toLowerCase()));
      });
    }
    if (excludeTags.length > 0) {
      list = list.filter((r) => {
        const name = (r.campaignName || '').toLowerCase();
        return !excludeTags.some(t => name.includes(t.toLowerCase()));
      });
    }
    return list;
  }, [googleData, filterTags, excludeTags]);

  const applyCostOverride = () => {
    const g = parseFloat(String(costInputGoogle).replace(/,/g, '.')) || null;
    const m = parseFloat(String(costInputMeta).replace(/,/g, '.')) || null;
    setCostOverrideGoogle(g);
    setCostOverrideMeta(m);
  };

  const clearCostOverride = () => {
    setCostInputGoogle('');
    setCostInputMeta('');
    setCostOverrideGoogle(null);
    setCostOverrideMeta(null);
  };

  const filteredMeta = useMemo(() => {
    let list = metaData;
    if (filterTags.length > 0) {
      list = list.filter((r) => {
        const name = (r.campaignName || '').toLowerCase();
        return filterTags.some(t => name.includes(t.toLowerCase()));
      });
    }
    if (excludeTags.length > 0) {
      list = list.filter((r) => {
        const name = (r.campaignName || '').toLowerCase();
        return !excludeTags.some(t => name.includes(t.toLowerCase()));
      });
    }
    return list;
  }, [metaData, filterTags, excludeTags]);

  if (!brandId) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
          <p className="text-amber-800 font-medium">Lütfen bir marka seçin.</p>
        </div>
      </div>
    );
  }

  if (brandId !== WUNDER_BRAND_ID) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
          <p className="text-amber-800 font-medium">Bu sayfa yalnızca <strong>Wunder</strong> markası için kullanılabilir.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Campaign Report</h1>
          <p className="text-sm text-gray-500 mt-1">Google Ads & Meta Ads · Filter by campaign name</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">Start</span>
            <DatePicker
              selected={startDate}
              onChange={(d) => {
                if (d) {
                  setStartDate(d);
                  if (endDate && d > endDate) setEndDate(d);
                }
              }}
              maxDate={endDate}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm"
              dateFormat="dd.MM.yyyy"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">End</span>
            <DatePicker
              selected={endDate}
              onChange={(d) => d && setEndDate(d)}
              minDate={startDate}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm"
              dateFormat="dd.MM.yyyy"
            />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Search className="w-4 h-4 text-gray-500 shrink-0" />
            {filterTags.map((tag, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-lg">
                {tag}
                <button onClick={() => setFilterTags(prev => prev.filter((_, j) => j !== i))} className="hover:text-blue-600">&times;</button>
              </span>
            ))}
            <input
              type="text"
              value={filterInput}
              onChange={(e) => setFilterInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && filterInput.trim()) {
                  e.preventDefault();
                  setFilterTags(prev => [...prev, filterInput.trim()]);
                  setFilterInput('');
                }
                if (e.key === 'Backspace' && !filterInput && filterTags.length > 0) {
                  setFilterTags(prev => prev.slice(0, -1));
                }
              }}
              placeholder={filterTags.length === 0 ? 'Include...' : ''}
              className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm w-32 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <FilterX className="w-4 h-4 text-gray-500 shrink-0" />
            {excludeTags.map((tag, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-lg">
                {tag}
                <button onClick={() => setExcludeTags(prev => prev.filter((_, j) => j !== i))} className="hover:text-red-600">&times;</button>
              </span>
            ))}
            <input
              type="text"
              value={excludeInput}
              onChange={(e) => setExcludeInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && excludeInput.trim()) {
                  e.preventDefault();
                  setExcludeTags(prev => [...prev, excludeInput.trim()]);
                  setExcludeInput('');
                }
                if (e.key === 'Backspace' && !excludeInput && excludeTags.length > 0) {
                  setExcludeTags(prev => prev.slice(0, -1));
                }
              }}
              placeholder={excludeTags.length === 0 ? 'Exclude...' : ''}
              className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm w-32 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <Card className="p-6 rounded-3xl bg-white border border-gray-200/80 shadow-sm">
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <>
            <MetricsTable
              title="Google Ads"
              icon="/google_ads-512.webp"
              alt="Google Ads"
              data={filteredGoogle}
              formatCurrency={formatCurrency}
              costOverride={costOverrideGoogle}
              isGoogleAds
            />
            <MetricsTable
              title="Meta Ads"
              icon="/6033716.png"
              alt="Meta Ads"
              data={filteredMeta}
              formatCurrency={formatCurrency}
              costOverride={costOverrideMeta}
              isGoogleAds={false}
            />

            <MonthlyPlanSection brandId={brandId} />

            <IkasBrandSalesSection brandId={brandId} startDate={startDate} endDate={endDate} />

          </>
        )}
      </Card>

      {/* Cost override panel - fixed bottom right, not centered */}
      <div
        className="fixed bottom-4 right-4 flex flex-col gap-2 p-3 rounded-xl border border-gray-200 bg-white/95 shadow-lg backdrop-blur-sm"
        style={{ zIndex: 50 }}
      >
        {(costOverrideGoogle != null || costOverrideMeta != null) && (
          <span className="text-[10px] text-amber-600 font-medium">Override active</span>
        )}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={costInputGoogle}
            onChange={(e) => setCostInputGoogle(e.target.value)}
            placeholder="Google cost"
            className="w-24 px-2 py-1.5 text-sm border border-gray-200 rounded-lg"
          />
          <input
            type="text"
            value={costInputMeta}
            onChange={(e) => setCostInputMeta(e.target.value)}
            placeholder="Meta cost"
            className="w-24 px-2 py-1.5 text-sm border border-gray-200 rounded-lg"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={applyCostOverride}
            className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Apply
          </button>
          <button
            onClick={clearCostOverride}
            className="px-3 py-1.5 text-xs font-medium bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  );
}
